import React, { useState } from 'react';
import { Plus, CheckCircle, Clock, AlertTriangle, Loader2 } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { toast } from 'react-hot-toast';

interface Task {
  id: string;
  title: string;
  description: string;
  assignee: {
    id: string;
    name: string;
    avatar: string;
  };
  dueDate: string;
  priority: 'high' | 'medium' | 'low';
  status: 'todo' | 'in_progress' | 'completed';
  comments: Array<{
    id: string;
    author: {
      name: string;
      avatar: string;
    };
    content: string;
    timestamp: string;
  }>;
}

interface TaskForm {
  title: string;
  description: string;
  assigneeId: string;
  dueDate: string;
  priority: Task['priority'];
}

const initialFormState: TaskForm = {
  title: '',
  description: '',
  assigneeId: '',
  dueDate: '',
  priority: 'medium',
};

interface TaskListProps {
  contractId: string;
}

export function TaskList({ contractId }: TaskListProps) {
  const [tasks, setTasks] = useState<Task[]>([
    {
      id: '1',
      title: 'Complete Security Assessment',
      description: 'Conduct comprehensive security assessment of current infrastructure',
      assignee: {
        id: '1',
        name: 'Michael Chen',
        avatar: 'https://ui-avatars.com/api/?name=Michael+Chen',
      },
      dueDate: '2024-03-15',
      priority: 'high',
      status: 'in_progress',
      comments: [
        {
          id: '1',
          author: {
            name: 'Sarah Johnson',
            avatar: 'https://ui-avatars.com/api/?name=Sarah+Johnson',
          },
          content: 'Please include cloud security controls in the assessment',
          timestamp: '2024-03-10T14:30:00Z',
        },
      ],
    },
  ]);

  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [form, setForm] = useState<TaskForm>(initialFormState);
  const [saving, setSaving] = useState(false);

  const handleNewTask = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      const newTask: Task = {
        id: crypto.randomUUID(),
        title: form.title,
        description: form.description,
        assignee: {
          id: form.assigneeId,
          name: 'Michael Chen', // This would come from a team members list
          avatar: 'https://ui-avatars.com/api/?name=Michael+Chen',
        },
        dueDate: form.dueDate,
        priority: form.priority,
        status: 'todo',
        comments: [],
      };

      setTasks(prev => [...prev, newTask]);
      setShowNewTaskModal(false);
      setForm(initialFormState);
      toast.success('Task created successfully');
    } catch (error) {
      toast.error('Failed to create task');
      console.error('Failed to create task:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStatus = async (task: Task, newStatus: Task['status']) => {
    setSaving(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      setTasks(prev =>
        prev.map(t =>
          t.id === task.id ? { ...t, status: newStatus } : t
        )
      );
      setShowUpdateStatusModal(false);
      setSelectedTask(null);
      toast.success('Task status updated successfully');
    } catch (error) {
      toast.error('Failed to update task status');
      console.error('Failed to update task status:', error);
    } finally {
      setSaving(false);
    }
  };

  const getPriorityIcon = (priority: Task['priority']) => {
    switch (priority) {
      case 'high':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'medium':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'low':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
    }
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'todo':
        return 'bg-gray-100 text-gray-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Tasks</h3>
        <button
          onClick={() => setShowNewTaskModal(true)}
          className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          <Plus className="mr-2 h-4 w-4" />
          New Task
        </button>
      </div>

      <div className="space-y-4">
        {tasks.map((task) => (
          <div
            key={task.id}
            className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {getPriorityIcon(task.priority)}
                <div>
                  <h4 className="text-lg font-medium text-gray-900">{task.title}</h4>
                  <p className="mt-1 text-sm text-gray-500">{task.description}</p>
                </div>
              </div>
              <span
                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(
                  task.status
                )}`}
              >
                {task.status.replace('_', ' ').charAt(0).toUpperCase() + task.status.slice(1)}
              </span>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <img
                    src={task.assignee.avatar}
                    alt={task.assignee.name}
                    className="h-6 w-6 rounded-full"
                  />
                  <span className="text-sm text-gray-500">{task.assignee.name}</span>
                </div>
                <span className="text-sm text-gray-500">
                  Due: {new Date(task.dueDate).toLocaleDateString()}
                </span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setSelectedTask(task);
                    setShowUpdateStatusModal(true);
                  }}
                  className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Update Status
                </button>
              </div>
            </div>

            {task.comments.length > 0 && (
              <div className="mt-4 border-t border-gray-200 pt-4">
                <h5 className="text-sm font-medium text-gray-700">Recent Comments</h5>
                <div className="mt-2 space-y-4">
                  {task.comments.map((comment) => (
                    <div key={comment.id} className="flex space-x-3">
                      <img
                        src={comment.author.avatar}
                        alt={comment.author.name}
                        className="h-6 w-6 rounded-full"
                      />
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">
                            {comment.author.name}
                          </span>
                          <span className="text-sm text-gray-500">
                            {new Date(comment.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500">{comment.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* New Task Modal */}
      <Modal
        isOpen={showNewTaskModal}
        onClose={() => setShowNewTaskModal(false)}
        title="Create New Task"
      >
        <form onSubmit={handleNewTask} className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Task Title
            </label>
            <input
              type="text"
              id="title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label htmlFor="assignee" className="block text-sm font-medium text-gray-700">
              Assignee
            </label>
            <select
              id="assignee"
              value={form.assigneeId}
              onChange={(e) => setForm({ ...form, assigneeId: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            >
              <option value="">Select assignee</option>
              <option value="1">Michael Chen</option>
              <option value="2">Sarah Johnson</option>
            </select>
          </div>

          <div>
            <label htmlFor="dueDate" className="block text-sm font-medium text-gray-700">
              Due Date
            </label>
            <input
              type="date"
              id="dueDate"
              value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            />
          </div>

          <div>
            <label htmlFor="priority" className="block text-sm font-medium text-gray-700">
              Priority
            </label>
            <select
              id="priority"
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value as Task['priority'] })}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              required
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setShowNewTaskModal(false)}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Task
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Update Status Modal */}
      <Modal
        isOpen={showUpdateStatusModal}
        onClose={() => setShowUpdateStatusModal(false)}
        title="Update Task Status"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Update the status for task: <strong>{selectedTask?.title}</strong>
          </p>

          <div className="grid grid-cols-3 gap-4">
            <button
              onClick={() => selectedTask && handleUpdateStatus(selectedTask, 'todo')}
              disabled={saving}
              className={`flex flex-col items-center rounded-lg border p-4 ${
                selectedTask?.status === 'todo'
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Clock className="h-6 w-6 text-gray-400" />
              <span className="mt-2 text-sm font-medium">Todo</span>
            </button>

            <button
              onClick={() => selectedTask && handleUpdateStatus(selectedTask, 'in_progress')}
              disabled={saving}
              className={`flex flex-col items-center rounded-lg border p-4 ${
                selectedTask?.status === 'in_progress'
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Clock className="h-6 w-6 text-yellow-500" />
              <span className="mt-2 text-sm font-medium">In Progress</span>
            </button>

            <button
              onClick={() => selectedTask && handleUpdateStatus(selectedTask, 'completed')}
              disabled={saving}
              className={`flex flex-col items-center rounded-lg border p-4 ${
                selectedTask?.status === 'completed'
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <CheckCircle className="h-6 w-6 text-green-500" />
              <span className="mt-2 text-sm font-medium">Completed</span>
            </button>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => setShowUpdateStatusModal(false)}
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}